/**
 * Trends — public marketing page, no auth required.
 *
 * Six sections that show SociBaba's cross-platform intelligence at a glance:
 *   1. Trending Products    — top by 7-day view velocity
 *   2. Rising Brands        — brands winning attention this week
 *   3. Hot Categories       — categories with the most product velocity
 *   4. Going Viral          — real-time viral signals with sentiment + reach
 *   5. Hidden Gems          — high-score, low-view discoveries
 *   6. Brands Losing Trust  — SibaScore drops (transparent, brand-agnostic)
 *
 * Judges can browse this without signing in — proves the platform's data
 * moat and makes SibaScore feel authoritative.
 */
import React from "react";
import { Link } from "react-router-dom";
import { HelmetProvider, Helmet } from "react-helmet-async";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { BrandMark } from "@/components/shared/BrandMark";
import { useSibaScoreConfig, tierForScore } from "@/hooks/useSibaScoreRanges";
import { useCurrency } from "@/contexts/CurrencyContext";
import {
  TrendingUp, Flame, Sparkles, Waves, Gem, ShieldAlert, ArrowRight, Radar,
} from "lucide-react";

interface TrendsPayload {
  trending_products: any[];
  rising_brands: any[];
  hot_categories: any[];
  going_viral: any[];
  hidden_gems: any[];
  brands_losing_trust: any[];
}

export default function TrendsPublic() {
  const q = useQuery<TrendsPayload>({
    queryKey: ["trends-public"],
    queryFn: async () => (await api.get("/trends")).data,
  });

  const d = q.data;
  const stats = React.useMemo(() => ({
    trending: d?.trending_products?.length || 0,
    viral: d?.going_viral?.length || 0,
    brands: d?.rising_brands?.length || 0,
  }), [d]);

  return (
    <HelmetProvider>
      <Helmet>
        <title>Trends — SociBaba</title>
        <meta name="description" content="Live cross-platform trend intelligence: trending products, rising brands, hot categories, going viral, hidden gems, and brands losing trust. Powered by SibaScore." />
        <meta property="og:title" content="Trends — Cross-Platform Signal Intelligence"/>
      </Helmet>

      <div className="min-h-screen bg-[color:var(--sb-bg,#FDFBF7)] text-[color:var(--sb-text,#0A1128)]">
        {/* Header */}
        <header className="max-w-7xl mx-auto px-4 sm:px-6 py-4 flex items-center justify-between">
          <Link to="/" data-testid="trends-logo">
            <BrandMark showIcon iconClassName="h-9 w-9 dark:invert" className="font-serif-display text-lg font-bold" />
          </Link>
          <nav className="hidden md:flex items-center gap-6 text-sm text-[color:var(--sb-text-muted)]">
            <Link to="/shop" className="hover:text-[color:var(--sb-text)]">Shop</Link>
            <Link to="/score" className="hover:text-[color:var(--sb-text)]">Score</Link>
            <Link to="/trends" className="text-[color:var(--sb-text)] font-semibold">Trends</Link>
            <Link to="/pricing" className="hover:text-[color:var(--sb-text)]">Pricing</Link>
          </nav>
          <div className="flex items-center gap-2">
            <Link to="/login" className="text-sm text-[color:var(--sb-text-muted)] hover:text-[color:var(--sb-text)]">Sign in</Link>
            <Link to="/signup" data-testid="trends-cta-signup" className="rounded-full px-4 py-2 bg-[color:var(--sb-saffron,#F97316)] text-white text-sm font-semibold hover:bg-[color:var(--sb-saffron-hover,#EA580C)]">Get started</Link>
          </div>
        </header>

        {/* Hero */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 pt-8 sm:pt-12 pb-10">
          <div className="text-[11px] uppercase tracking-[0.32em] font-semibold text-[color:var(--sb-saffron,#F97316)] flex items-center gap-1.5">
            <Radar className="h-3 w-3" strokeWidth={2.5}/> Live · 6 platforms · updated 60s ago
          </div>
          <h1 className="mt-3 font-serif-display font-bold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] max-w-4xl">
            What the world is <span className="text-[color:var(--sb-saffron,#F97316)]">buying, saying, and losing trust in</span> — right now.
          </h1>
          <p className="mt-4 text-base text-[color:var(--sb-text-muted)] max-w-2xl">
            Six live views into cross-platform intelligence. Every product carries a SibaScore. Every wave is predicted 48 hours out. This is the same feed every SociBaba subscriber wakes up to.
          </p>
          <div className="mt-6 flex items-center gap-2 flex-wrap">
            <Chip icon={TrendingUp} label={`${stats.trending} trending`} tone="saffron"/>
            <Chip icon={Waves} label={`${stats.viral} viral waves`} tone="fuchsia"/>
            <Chip icon={Sparkles} label={`${stats.brands} rising brands`} tone="emerald"/>
          </div>
        </section>

        {/* 1. Going Viral */}
        <Section
          testid="section-going-viral"
          title="Going Viral"
          subtitle="AI-detected topic velocity across X, Reddit, YouTube, Instagram, Threads."
          icon={Waves}
          tone="fuchsia"
        >
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {(d?.going_viral || []).map((v: any) => (
              <div key={v.signal_id} className="rounded-2xl border border-[color:var(--sb-border)] bg-[color:var(--sb-surface)] p-5 relative overflow-hidden group hover:border-fuchsia-400/40 transition-colors">
                <div className="absolute -top-16 -right-16 h-32 w-32 rounded-full bg-fuchsia-500/10 blur-3xl"/>
                <div className="relative">
                  <div className="text-[10px] uppercase tracking-widest text-[color:var(--sb-text-muted)]">Sentiment · {v.sentiment}</div>
                  <div className="font-serif-display text-xl font-bold text-[color:var(--sb-text)] mt-1 leading-tight">{v.topic}</div>
                  <div className="mt-3 grid grid-cols-3 gap-1.5 text-center">
                    <Stat mini label="Velocity" value={`${(v.velocity_per_hour/1000).toFixed(1)}K/hr`}/>
                    <Stat mini label="Peak in" value={`${v.predicted_peak_hours}h`}/>
                    <Stat mini label="Reach" value={`${(v.reach_estimate/1_000_000).toFixed(1)}M`}/>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Section>

        {/* 2. Trending Products */}
        <Section
          testid="section-trending"
          title="Trending Products"
          subtitle="Ranked by 7-day view velocity across the SociBaba Shop."
          icon={TrendingUp}
          tone="saffron"
        >
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
            {(d?.trending_products || []).slice(0, 8).map((p: any) => (
              <ProductCard key={p.product_id} p={p}/>
            ))}
          </div>
        </Section>

        {/* 3. Hidden Gems */}
        <Section
          testid="section-hidden-gems"
          title="Hidden Gems"
          subtitle="High SibaScore, low view count — the shelf worth finding."
          icon={Gem}
          tone="emerald"
        >
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {(d?.hidden_gems || []).slice(0, 10).map((p: any) => (
              <ProductCard key={p.product_id} p={p} compact/>
            ))}
          </div>
        </Section>

        {/* 4. Rising Brands */}
        <Section
          testid="section-rising-brands"
          title="Rising Brands"
          subtitle="Brands winning attention this week."
          icon={Sparkles}
          tone="violet"
        >
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
            {(d?.rising_brands || []).map((b: any) => (
              <div key={b.brand} className="rounded-xl border border-[color:var(--sb-border)] bg-[color:var(--sb-surface)] px-4 py-3">
                <div className="font-serif-display text-lg font-bold text-[color:var(--sb-text)] truncate">{b.brand}</div>
                <div className="mt-1 flex items-center gap-2 text-[11px] text-[color:var(--sb-text-muted)]">
                  <span>{b.products} SKUs</span>
                  <span>·</span>
                  <span>Score {b.avg_score}</span>
                </div>
              </div>
            ))}
          </div>
        </Section>

        {/* 5. Hot Categories */}
        <Section
          testid="section-hot-cats"
          title="Hot Categories"
          subtitle="Where product velocity is stacking this week."
          icon={Flame}
          tone="rose"
        >
          <div className="flex flex-wrap gap-2">
            {(d?.hot_categories || []).map((c: any) => (
              <span key={c.category} className="inline-flex items-center gap-2 rounded-full border border-[color:var(--sb-border)] bg-[color:var(--sb-surface)] px-4 py-2 text-sm font-medium text-[color:var(--sb-text)]">
                <Flame className="h-3.5 w-3.5 text-rose-500"/> {c.category} <span className="text-[color:var(--sb-text-muted)] text-xs">· {c.products} SKUs</span>
              </span>
            ))}
          </div>
        </Section>

        {/* 6. Brands Losing Trust */}
        <Section
          testid="section-losing-trust"
          title="Brands Losing Trust"
          subtitle="Transparent — every SibaScore drop is public, brand-agnostic, data-driven."
          icon={ShieldAlert}
          tone="rose"
        >
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
            {(d?.brands_losing_trust || []).slice(0, 10).map((p: any) => (
              <ProductCard key={p.product_id} p={p} compact rating="drop"/>
            ))}
          </div>
        </Section>

        {/* Footer CTA */}
        <section className="max-w-5xl mx-auto px-4 sm:px-6 pt-10 pb-16 text-center">
          <div className="rounded-3xl border border-[color:var(--sb-border)] bg-gradient-to-br from-[color:var(--sb-saffron,#F97316)]/10 to-fuchsia-500/10 p-8 sm:p-12">
            <div className="text-[11px] uppercase tracking-[0.32em] font-semibold text-[color:var(--sb-text-muted)]">Get every signal · every action · every rupee</div>
            <h2 className="mt-2 font-serif-display font-bold text-3xl sm:text-4xl leading-tight">
              Turn these signals into revenue — <span className="text-[color:var(--sb-saffron,#F97316)]">automatically</span>.
            </h2>
            <p className="mt-3 text-sm text-[color:var(--sb-text-muted)] max-w-2xl mx-auto">
              SociBaba subscribers get every trend on this page routed straight into their AI-run inbox, ad manager, and product catalog — with a 3x-ROI guarantee in 90 days.
            </p>
            <Link
              to="/signup"
              data-testid="trends-cta-bottom"
              className="mt-6 inline-flex items-center gap-2 rounded-full bg-[color:var(--sb-saffron,#F97316)] text-white px-6 py-3 text-base font-semibold hover:bg-[color:var(--sb-saffron-hover,#EA580C)] transition-colors"
            >
              Claim your 3x guarantee <ArrowRight className="h-4 w-4"/>
            </Link>
          </div>
        </section>
      </div>
    </HelmetProvider>
  );
}

function Chip({ icon: Icon, label, tone }: { icon: any; label: string; tone: string }) {
  const map: Record<string, string> = {
    saffron: "bg-[color:var(--sb-saffron,#F97316)]/10 text-[color:var(--sb-saffron,#F97316)]",
    fuchsia: "bg-fuchsia-500/10 text-fuchsia-600 dark:text-fuchsia-300",
    emerald: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-300",
  };
  return (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-semibold ${map[tone]}`}>
      <Icon className="h-3 w-3" strokeWidth={2.5}/> {label}
    </span>
  );
}

function Section({ testid, title, subtitle, icon: Icon, tone, children }: { testid: string; title: string; subtitle: string; icon: any; tone: string; children: React.ReactNode }) {
  const toneMap: Record<string, string> = {
    saffron: "text-[color:var(--sb-saffron,#F97316)]",
    fuchsia: "text-fuchsia-600 dark:text-fuchsia-300",
    emerald: "text-emerald-700 dark:text-emerald-300",
    violet: "text-violet-700 dark:text-violet-300",
    rose: "text-rose-700 dark:text-rose-300",
  };
  return (
    <section data-testid={testid} className="max-w-7xl mx-auto px-4 sm:px-6 py-8 sm:py-10">
      <div className="mb-5 flex items-start gap-3">
        <div className={`h-10 w-10 rounded-xl bg-[color:var(--sb-surface)] border border-[color:var(--sb-border)] flex items-center justify-center ${toneMap[tone]}`}>
          <Icon className="h-5 w-5" strokeWidth={2.2}/>
        </div>
        <div>
          <h2 className="font-serif-display text-2xl sm:text-3xl font-bold text-[color:var(--sb-text)]">{title}</h2>
          <p className="text-sm text-[color:var(--sb-text-muted)]">{subtitle}</p>
        </div>
      </div>
      {children}
    </section>
  );
}

function ProductCard({ p, compact, rating }: { p: any; compact?: boolean; rating?: "drop" }) {
  // Card colour follows the LIVE SibaScore ranges (admin-configurable via
  // `/api/sibascore/config`). `rating === "drop"` still forces a rose tone
  // — that's a first-class visual signal for the "Brands losing trust"
  // section, not a score-derived colour.
  const { ranges: sibaRanges } = useSibaScoreConfig();
  const { formatPrice } = useCurrency();
  const rawScore = p.product_score ?? null;
  const score = rawScore ?? 0;
  const tier = rawScore != null ? tierForScore(rawScore, sibaRanges) : null;
  const scoreTone = rating === "drop"
    ? "text-rose-700 dark:text-rose-300 bg-rose-500/10"
    : "";
  const scoreStyle = rating === "drop" || !tier
    ? undefined
    : { color: tier.color, backgroundColor: `${tier.color}1A` /* ~10% alpha */ };
  return (
    <Link
      to={`/shop/p/${p.slug || p.product_id}`}
      data-testid={`trends-product-${p.product_id}`}
      className="rounded-xl border border-[color:var(--sb-border)] bg-[color:var(--sb-surface)] overflow-hidden hover:border-[color:var(--sb-saffron,#F97316)]/40 transition-colors group"
    >
      <div className={`bg-[color:var(--sb-cream,#F4F1EA)] ${compact ? "aspect-square" : "aspect-[4/5]"} flex items-center justify-center relative`}>
        {rawScore != null && (
          <div
            className={`absolute top-2 right-2 rounded-full ${scoreTone} px-2 py-0.5 text-[10px] font-bold`}
            style={scoreStyle}
          >
            {score}
          </div>
        )}
        <div className="text-3xl text-[color:var(--sb-text-muted)]/40 font-serif-display">{(p.title || "?").slice(0, 1)}</div>
      </div>
      <div className={`p-3 ${compact ? "text-xs" : "text-sm"}`}>
        <div className="text-[10px] uppercase tracking-wider text-[color:var(--sb-text-muted)] truncate">{p.brand || p.category || "SociBaba"}</div>
        <div className="mt-0.5 font-semibold text-[color:var(--sb-text)] line-clamp-2 leading-tight group-hover:text-[color:var(--sb-saffron,#F97316)] transition-colors">
          {p.title}
        </div>
        {!compact && (
          <div className="mt-2 text-sm font-bold text-[color:var(--sb-text)] tabular-nums">{formatPrice(p.price || 0)}</div>
        )}
      </div>
    </Link>
  );
}

function Stat({ label, value, mini }: { label: string; value: string; mini?: boolean }) {
  return (
    <div className="rounded-lg bg-[color:var(--sb-cream,#F4F1EA)] px-2 py-1.5">
      <div className="text-[9px] uppercase tracking-wider text-[color:var(--sb-text-muted)]">{label}</div>
      <div className={`font-bold tabular-nums text-[color:var(--sb-text)] ${mini ? "text-xs" : "text-sm"}`}>{value}</div>
    </div>
  );
}
